import { useState } from "react";
import axios from "axios";
import { MessageCircle, Mail, Phone, CheckCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { API, EMAIL, PHONE_DISPLAY, WHATSAPP_URL } from "../data/content";

const EVENT_TYPES = ["Corporate Catering", "Private Event", "Event Catering (50+ guests)", "Cheeseboards & Grazing", "Braai & Social", "Platters & Finger Foods", "Gift & Occasion Boxes", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], guests: "", event_date: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const payload = { ...form, guests: form.guests ? parseInt(form.guests, 10) : null };
      const { data } = await axios.post(`${API}/enquiries`, payload);
      setSent(data);
    } catch {
      setError("Could not send your enquiry. Please try WhatsApp or email instead.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-[#1A1A1A] border border-[#F9F9F6]/15 focus:border-[#D4AF37] px-4 py-3.5 text-sm text-[#F9F9F6] placeholder:text-[#F9F9F6]/30 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#1A1A1A] py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Let's Talk</p>
          <h2 className="font-serif-e text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#F9F9F6] leading-tight">
            Tell us about your occasion.
          </h2>
          <p className="text-[#F9F9F6]/60 text-base leading-relaxed mt-6 max-w-md">
            Consultation, menu recommendation, quotation, preparation, packaging, delivery — we make catering seamless, professional and stress-free.
          </p>
          <div className="mt-10 space-y-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
               className="flex items-center gap-4 border border-[#F9F9F6]/15 px-6 py-4 hover:border-[#D4AF37]/60 transition-colors group">
              <MessageCircle size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F9F6]/40">WhatsApp</p>
                <p className="text-[#F9F9F6] group-hover:text-[#D4AF37] transition-colors">{PHONE_DISPLAY}</p>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-btn"
               className="flex items-center gap-4 border border-[#F9F9F6]/15 px-6 py-4 hover:border-[#D4AF37]/60 transition-colors group">
              <Mail size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F9F6]/40">Email</p>
                <p className="text-[#F9F9F6] group-hover:text-[#D4AF37] transition-colors">{EMAIL}</p>
              </div>
            </a>
            <div className="flex items-center gap-4 border border-[#F9F9F6]/15 px-6 py-4">
              <Phone size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#F9F9F6]/40">Collection Point</p>
                <p className="text-[#F9F9F6]">1 Coldstream St, Little Falls, Roodepoort</p>
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          {sent ? (
            <div className="border border-[#D4AF37]/40 bg-[#0A0A0A] p-10 text-center h-full flex flex-col items-center justify-center" data-testid="enquiry-success">
              <CheckCircle size={44} strokeWidth={1} className="text-[#D4AF37]" />
              <h3 className="font-serif-e text-3xl text-[#F9F9F6] mt-6">Enquiry received.</h3>
              <p className="text-[#F9F9F6]/60 text-sm mt-3 max-w-sm leading-relaxed">
                Thank you, {sent.enquiry.name}. We'll be in touch shortly. For a faster response, send it to us on WhatsApp too.
              </p>
              <a href={sent.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp-link"
                 className="mt-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#F9F9F6] transition-colors">
                Send via WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="border border-[#F9F9F6]/10 bg-[#0A0A0A] p-8 md:p-10 grid sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name-input" className={field} />
              <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email-input" className={field} />
              <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone-input" className={field} />
              <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="quote-event-type-select" className={field}>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" min="1" placeholder="Number of guests" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="quote-guests-input" className={field} />
              <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="quote-date-input" className={`${field} [color-scheme:dark]`} />
              <textarea required placeholder="Tell us about your event — location, style, menu ideas, budget…" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quote-message-input" className={`${field} sm:col-span-2 resize-none`} />
              {error && <p data-testid="quote-error" className="sm:col-span-2 text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={sending} data-testid="quote-submit-btn"
                      className="sm:col-span-2 rounded-full bg-[#D4AF37] text-[#0A0A0A] py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#F9F9F6] transition-colors disabled:opacity-50">
                {sending ? "Sending…" : "Request a Quote"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
